import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Alert,
  Button,
  Card,
  Col,
  Progress,
  Row,
  Select,
  Space,
  Statistic,
  Table,
  Tabs,
  Tag,
  Typography,
} from "antd";
import {
  CloudServerOutlined,
  DatabaseOutlined,
  ReloadOutlined,
  RobotOutlined,
  UserOutlined,
} from "@ant-design/icons";
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  systemApi,
  type SystemContainerStatus,
  type TrafficStatsResponse,
} from "../services/api";

const { Title, Text } = Typography;

const formatBytes = (value: number) => {
  if (!value) return "0 B";
  const units = ["B", "KB", "MB", "GB", "TB"];
  const unit = Math.min(Math.floor(Math.log(value) / Math.log(1024)), units.length - 1);
  return `${(value / 1024 ** unit).toFixed(unit > 1 ? 1 : 0)} ${units[unit]}`;
};

const visitorLabels: Record<string, string> = {
  human: "Ludzie",
  search_bot: "Boty wyszukiwarek",
  ai_bot: "Boty AI",
  other_bot: "Pozostałe boty",
};

function SystemStatusTab() {
  const query = useQuery({
    queryKey: ["system-status"],
    queryFn: systemApi.status,
    refetchInterval: 30000,
  });
  const data = query.data;

  return (
    <Space direction="vertical" size="large" style={{ width: "100%" }}>
      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        <Button
          icon={<ReloadOutlined />}
          loading={query.isFetching}
          onClick={() => void query.refetch()}
        >
          Odśwież
        </Button>
      </div>

      {query.isError && (
        <Alert type="error" showIcon message="Nie udało się pobrać stanu systemu" />
      )}

      {data && (
        <>
          <Row gutter={[16, 16]}>
            <Col xs={24} md={8}>
              <Card>
                <Statistic
                  title={`Baza danych (${data.database.name})`}
                  value={formatBytes(data.database.size_bytes)}
                  prefix={<DatabaseOutlined />}
                />
                <Text type="secondary">
                  Połączenia: {data.database.connections}/{data.database.max_connections}
                </Text>
              </Card>
            </Col>
            <Col xs={24} md={8}>
              <Card>
                <Statistic
                  title="Dysk magazynu obrazów"
                  value={formatBytes(data.storage.used_bytes)}
                  suffix={`/ ${formatBytes(data.storage.total_bytes)}`}
                  prefix={<CloudServerOutlined />}
                />
                <Progress percent={data.storage.used_percent} size="small" />
              </Card>
            </Col>
            <Col xs={24} md={8}>
              <Card>
                <Statistic
                  title="Kontenery"
                  value={data.docker.containers.filter((item) => item.state === "running").length}
                  suffix={`/ ${data.docker.containers.length}`}
                  prefix={<CloudServerOutlined />}
                />
                <Text type="secondary">uruchomione / wszystkie</Text>
              </Card>
            </Col>
          </Row>

          {!data.docker.available && (
            <Alert
              type="warning"
              showIcon
              message="Metryki Dockera są niedostępne"
              description={data.docker.error}
            />
          )}

          <Card title="Kontenery">
            <Table<SystemContainerStatus>
              rowKey="id"
              pagination={false}
              scroll={{ x: 950 }}
              dataSource={data.docker.containers}
              columns={[
                {
                  title: "Kontener",
                  dataIndex: "name",
                  render: (name, row) => (
                    <Space direction="vertical" size={0}>
                      <Text strong>{name}</Text>
                      <Text type="secondary" style={{ fontSize: 12 }}>{row.image}</Text>
                    </Space>
                  ),
                },
                {
                  title: "Stan",
                  dataIndex: "state",
                  render: (state) => (
                    <Tag color={state === "running" ? "green" : "default"}>{state}</Tag>
                  ),
                },
                {
                  title: "CPU",
                  dataIndex: "cpu_percent",
                  render: (value) => `${value.toFixed(2)}%`,
                },
                {
                  title: "RAM",
                  render: (_, row) => (
                    <Space direction="vertical" size={0} style={{ minWidth: 180 }}>
                      <Text>
                        {formatBytes(row.memory_usage_bytes)} / {formatBytes(row.memory_limit_bytes)}
                      </Text>
                      <Progress percent={Math.min(row.memory_percent, 100)} size="small" />
                    </Space>
                  ),
                },
                {
                  title: "Warstwa zapisywalna",
                  dataIndex: "disk_writable_bytes",
                  render: formatBytes,
                },
                {
                  title: "Rozmiar z obrazem",
                  dataIndex: "disk_root_bytes",
                  render: formatBytes,
                },
                { title: "Status", dataIndex: "status" },
              ]}
            />
          </Card>

          <Card title="Liczba wierszy w tabelach">
            <Table
              rowKey="table"
              pagination={{ pageSize: 20 }}
              dataSource={data.database.tables}
              columns={[
                { title: "Tabela", dataIndex: "table", sorter: (a, b) => a.table.localeCompare(b.table) },
                {
                  title: "Wiersze",
                  dataIndex: "rows",
                  align: "right",
                  sorter: (a, b) => a.rows - b.rows,
                  render: (value: number) => value.toLocaleString("pl-PL"),
                },
              ]}
            />
          </Card>
        </>
      )}
    </Space>
  );
}

function TrafficTab() {
  const [days, setDays] = useState(30);
  const query = useQuery({
    queryKey: ["system-traffic", days],
    queryFn: () => systemApi.traffic(days),
  });
  const data = query.data;

  const cards: Array<{
    key: keyof TrafficStatsResponse["totals"];
    title: string;
    color: string;
    icon: React.ReactNode;
  }> = [
    { key: "human", title: "Odsłony ludzi", color: "#1677ff", icon: <UserOutlined /> },
    { key: "search_bot", title: "Wyszukiwarki", color: "#52c41a", icon: <RobotOutlined /> },
    { key: "ai_bot", title: "Boty AI", color: "#722ed1", icon: <RobotOutlined /> },
    { key: "other_bot", title: "Inne boty", color: "#8c8c8c", icon: <RobotOutlined /> },
  ];

  return (
    <Space direction="vertical" size="large" style={{ width: "100%" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Text type="secondary">
          Ludzie są liczeni po zgodzie analitycznej; boty po logach Nginx.
        </Text>
        <Space>
          <Select
            value={days}
            onChange={setDays}
            options={[
              { value: 7, label: "7 dni" },
              { value: 30, label: "30 dni" },
              { value: 90, label: "90 dni" },
              { value: 365, label: "365 dni" },
            ]}
          />
          <Button
            icon={<ReloadOutlined />}
            loading={query.isFetching}
            onClick={() => void query.refetch()}
          >
            Odśwież
          </Button>
        </Space>
      </div>

      {query.isError && (
        <Alert type="error" showIcon message="Nie udało się pobrać statystyk ruchu" />
      )}

      {data && (
        <>
          <Row gutter={[16, 16]}>
            {cards.map((card) => (
              <Col xs={24} sm={12} xl={6} key={card.key}>
                <Card>
                  <Statistic
                    title={card.title}
                    value={data.totals[card.key]}
                    prefix={card.icon}
                    valueStyle={{ color: card.color }}
                  />
                </Card>
              </Col>
            ))}
          </Row>

          <Alert
            type="info"
            showIcon
            message={`Unikalni użytkownicy (anonimowo): ${data.unique_humans.toLocaleString("pl-PL")}`}
            description={
              data.synced_bot_events > 0
                ? `Przy tym odświeżeniu zaimportowano ${data.synced_bot_events} nowych wejść botów.`
                : "Logi botów są zsynchronizowane."
            }
          />

          <Card title="Ruch w czasie">
            <div style={{ width: "100%", height: 340 }}>
              <ResponsiveContainer>
                <LineChart data={data.daily}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis allowDecimals={false} />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="human" name="Ludzie" stroke="#1677ff" />
                  <Line type="monotone" dataKey="search_bot" name="Wyszukiwarki" stroke="#52c41a" />
                  <Line type="monotone" dataKey="ai_bot" name="AI" stroke="#722ed1" />
                  <Line type="monotone" dataKey="other_bot" name="Inne boty" stroke="#8c8c8c" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </Card>

          <Row gutter={[16, 16]}>
            <Col xs={24} lg={12}>
              <Card title="Najczęściej odwiedzane strony">
                <Table
                  rowKey="path"
                  pagination={false}
                  dataSource={data.top_paths}
                  columns={[
                    { title: "Ścieżka", dataIndex: "path" },
                    { title: "Odsłony", dataIndex: "visits", align: "right" },
                  ]}
                />
              </Card>
            </Col>
            <Col xs={24} lg={12}>
              <Card title="Rozpoznane boty">
                <Table
                  rowKey={(row) => `${row.visitor_type}:${row.name}`}
                  pagination={false}
                  dataSource={data.bots}
                  columns={[
                    {
                      title: "Typ",
                      dataIndex: "visitor_type",
                      render: (value) => visitorLabels[value] || value,
                    },
                    { title: "Bot", dataIndex: "name" },
                    { title: "Wejścia", dataIndex: "visits", align: "right" },
                  ]}
                />
              </Card>
            </Col>
          </Row>
        </>
      )}
    </Space>
  );
}

export function SystemPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const activeTab = location.pathname.endsWith("/traffic")
    ? "traffic"
    : "status";

  return (
    <div>
      <Title level={2}>System</Title>
      <Tabs
        activeKey={activeTab}
        onChange={(key) => navigate(`/system/${key}`)}
        items={[
          { key: "status", label: "Stan systemu", children: <SystemStatusTab /> },
          { key: "traffic", label: "Statystyki odwiedzin", children: <TrafficTab /> },
        ]}
      />
    </div>
  );
}
